import { FormikProps } from "formik";
import React, { FunctionComponent, memo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { ICodelist } from "@/data/codelists";
import { ILocation } from "@/data/locations";
import { IPhone, TPhoneCodeCodelistParameter } from "@/data/phones";
import { Flex, Group, ReactSelectComponents } from "@/eworkitComponents";
import { FormikTextInput } from "@/formikComponents";
import { ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { FormikCodelistGenericSelect } from "@/views/components/Formik";

import "./_styles.scss";

export const validationPhoneSchema = Yup.object().shape({
  phone: Yup.object().shape(
    {
      phoneNumber: Yup.string().when("phoneCode", {
        is: (val: any) => !!val,
        then: Yup.string()
          .required(VALIDATION_TYPES.REQUIRED)
          .test({
            message: VALIDATION_TYPES.NUMBERS,
            test: (phone: any) => !isNaN(phone),
          }),
        otherwise: Yup.string(),
      }),
      phoneCode: Yup.string()
        .nullable(true)
        .when("phoneNumber", {
          is: (val: any) => !!val,
          then: Yup.string().required(VALIDATION_TYPES.REQUIRED),
          otherwise: Yup.string().nullable(true),
        }),
    },
    [
      ["phoneCode", "phoneNumber"],
      ["phoneNumber", "phoneCode"],
    ],
  ),
});

export interface IPhoneFormValues {
  phone: IPhone;
}

export const PHONE_FORM_INITIAL_VALUES: IPhoneFormValues = {
  phone: {
    phoneCode: null,
    phoneNumber: "",
    phonePrefix: "",
  },
};

export interface IPhoneFormProps {
  form: FormikProps<IPhoneFormValues>;
  withLabels?: boolean;
  size?: string;
  location?: ILocation | null;
}

const PHONE_CODES_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "phone-codes" };

export const PhoneForm: FunctionComponent<IPhoneFormProps> = memo(({ form, withLabels = true, size, location }) => {
  const { values } = form;
  const { t } = useTranslation("session");

  useEffect(() => {
    if (location && !form.values.phone.phoneNumber) {
      form.setFieldValue("phone.phoneCode", location.suggestedPhoneCode);
    }
  }, [form.setFieldValue, location, form.values.phone.phoneNumber]);

  const setParameterAsValue = useCallback(
    (option: Array<ICodelist<TPhoneCodeCodelistParameter>>) =>
      option.map(o => ({
        ...o,
        value: o.parameter.format,
      })),
    [],
  );

  return (
    <Flex direction="row" innerMargin="sm" align="flex-start">
      <Group
        label={withLabels ? t("COMPONENTS.PHONE_FORM.PLACEHOLDER_PHONE_CODE") : undefined}
        width={5}
        direction="column">
        <FormikCodelistGenericSelect
          size={size}
          className="phone-code-select"
          simpleValue
          value={values.phone.phoneCode}
          name="phone.phoneCode"
          testName="phone.phoneCode"
          required
          query={PHONE_CODES_CODELIST_QUERY}
          placeholder={t("COMPONENTS.PHONE_FORM.COUNTRY_SELECT_PLACEHOLDER")}
          components={{
            SingleValue: ({ children, ...props }) => {
              return (
                <ReactSelectComponents.SingleValue {...props}>
                  {props.data.parameter.format}
                </ReactSelectComponents.SingleValue>
              );
            },
          }}
          filterAfterFetch={setParameterAsValue}
        />
        <FormikError name="phone.phoneCode" data-test="error-phone.phoneCode" />
      </Group>
      <Group
        label={withLabels ? t("COMPONENTS.PHONE_FORM.PLACEHOLDER_PHONE_NUMBER") : undefined}
        width={7}
        direction="column">
        <FormikTextInput
          className="phone-number-input"
          required
          name="phone.phoneNumber"
          testName="phone.phoneNumber"
          value={values.phone.phoneNumber}
          size={size}
        />
        <FormikError name="phone.phoneNumber" data-test="error-phone.phoneNumber" />
      </Group>
    </Flex>
  );
});
